import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import '@/style/news.scss'
import PageHero from '@/components/PageHero'
import Reveal from '@/components/Reveal'
import heroImg from '@/images/site/hero-news.jpg'
import { fetchNewsListSafe } from '@/api/news'
import sanitize, { stripTags } from '@/utils/sanitize'

const PAGE_SIZE = 8

function News() {
  const navigate = useNavigate()
  const [list, setList] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async (p) => {
    setLoading(true)
    const { list: rows, total: count } = await fetchNewsListSafe({ size: PAGE_SIZE, start: p })
    setList(rows)
    setTotal(count)
    setLoading(false)
  }, [])

  useEffect(() => {
    load(page)
  }, [page, load])

  // 带 ?type=news 时滚动定位
  useEffect(() => {
    const type = new URLSearchParams(window.location.search).get('type')
    if (type !== 'news') return
    document.getElementById('news')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const openDetail = (item) => {
    navigate(`/news/detail/${item.id}`, { state: item })
  }

  const pages = Math.ceil(total / PAGE_SIZE)

  return (
    <>
      <PageHero
        eyebrow="Newsroom"
        title="新闻中心"
        image={heroImg}
      />

      <section className="section news__section" id="news">
        <div className="mainContent">
          <Reveal className="secHead">
            <p className="secHead__eyebrow">Latest</p>
            <h2 className="secHead__title">公司动态</h2>
          </Reveal>

          {loading && (
            <div className="news__grid">
              {Array.from({ length: 3 }).map((_, i) => (
                <div className="newsCard newsCard--skeleton" key={i}>
                  <div className="newsCard__media" />
                  <div className="newsCard__body">
                    <div className="sk sk--title" />
                    <div className="sk sk--line" />
                    <div className="sk sk--line sk--short" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && list.length > 0 && (
            <div className="news__grid">
              {list.map((item, i) => (
                <Reveal key={item.id ?? i} delay={(i % 3) * 90}>
                  <button className="newsCard" type="button" onClick={() => openDetail(item)}>
                    {item.titlePicUrl && (
                      <div className="newsCard__media">
                        <img src={item.titlePicUrl} alt={item.msgTitle} loading="lazy" />
                      </div>
                    )}
                    <div className="newsCard__body">
                      <time className="newsCard__date">{item.releaseTime}</time>
                      <h3 className="newsCard__title">{item.msgTitle}</h3>
                      <p className="newsCard__desc">{stripTags(sanitize(item.msgSummary))}</p>
                      <span className="newsCard__more">
                        阅读全文
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M1 7h11M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}

          {!loading && !list.length && (
            <div className="noDataBox">
              <i />
              <p>暂无动态</p>
            </div>
          )}

          {pages > 1 && (
            <nav className="news__pager" aria-label="分页">
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={page === i + 1 ? 'active' : ''}
                  aria-current={page === i + 1 ? 'page' : undefined}
                  onClick={() => setPage(i + 1)}
                >
                  {String(i + 1).padStart(2, '0')}
                </button>
              ))}
            </nav>
          )}
        </div>
      </section>
    </>
  )
}

export default News